import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Building2,
  Plus,
  X,
  Users,
  Coffee,
  ShoppingCart,
  LogIn,
  RefreshCw,
  AlertCircle,
  Eye,
  Search,
  Edit2,
  Check,
  ChevronDown,
  Trash2,
  UtensilsCrossed,
  Image as ImageIcon,
  Upload,
  KeyRound,
  MapPin,
  LocateFixed,
  ExternalLink
} from 'lucide-react';
import Modal from '../components/Modal';
import api from '../config/api';
import { showNotification } from '../components/GlobalNotification';

const EMPTY_FORM = {
  tenantId: '',
  tenantName: '',
  tenantPan: '',
  tenantMapsUrl: '',
  tenantLatitude: '',
  tenantLongitude: '',
  username: '',
  email: '',
  password: '',
  address: '',
  phoneNumber: ''
};

const EMPTY_EDIT = {
  tenantName: '',
  tenantPan: '',
  tenantMapsUrl: '',
  tenantLatitude: '',
  tenantLongitude: ''
};

/**
 * A link that opens the shop on a map, or null when nothing was recorded.
 *
 * A pasted Google listing wins over coordinates: it is the shop's own entry,
 * with its name and photos, while coordinates only ever produce a dropped pin.
 * The protocol is checked again here - the server refuses anything but http(s),
 * but this is what actually becomes an href, so it guards itself.
 */
function mapsLinkFor(tenant) {
  const url = (tenant?.tenantMapsUrl || '').trim();

  if (url) {
    try {
      const parsed = new URL(url);
      if (parsed.protocol === 'https:' || parsed.protocol === 'http:') return url;
    } catch {
      // Unparseable - fall through to the coordinates
    }
  }

  const { tenantLatitude: lat, tenantLongitude: lng } = tenant || {};
  if (typeof lat === 'number' && typeof lng === 'number') {
    return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
  }

  return null;
}

/**
 * The super admin's landing page: the system console that sits above every
 * tenant, rather than the till dashboard a tenant sees.
 *
 * A tenant is created here together with the admin who will run it, because the
 * two are useless apart - a tenant with no admin cannot be logged into, and
 * creating the admin as a separate step is exactly where the tenant id gets
 * mistyped and a second, empty tenant silently appears.
 */
function SystemDashboard() {
  const navigate = useNavigate();
  const { user, setTenantId, getCurrentTenantId } = useAuth();

  /**
   * A sales agent uses the same console for the one job they have - registering
   * businesses - and sees nothing else: only the ones they registered, no
   * revenue, and no way into anybody's till. The API enforces all three; this
   * only keeps controls that would fail out of their way.
   */
  const isSalesAgent = user?.role === 'sales';

  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [search, setSearch] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Inline edit: which business is open for editing, and the details being typed.
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_EDIT);
  const [savingEdit, setSavingEdit] = useState(false);
  // Which business's logins are open in the reset-password dialog
  const [passwordTenant, setPasswordTenant] = useState(null);

  const activeTenantId = getCurrentTenantId();

  const fetchTenants = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const response = await api.get('/api/tenants');
      setTenants(response.data);
    } catch (error) {
      setLoadError(error.response?.data?.message || 'Could not load tenants');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTenants();
  }, [fetchTenants]);

  /**
   * The tenant id is the permanent key every order, item and expense is stamped
   * with, so it is derived from the name rather than typed twice. It stays
   * editable - but a suggestion that already matches the server's rules is what
   * keeps `cafe-downtown` from becoming `Cafe Downtown` on the second attempt.
   */
  const handleNameChange = (event) => {
    const tenantName = event.target.value;

    setForm((current) => {
      const suggested = tenantName
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 50);

      // Only follow the name while the id has not been edited by hand.
      const idWasAuto = current.tenantId === slugify(current.tenantName);

      return {
        ...current,
        tenantName,
        tenantId: idWasAuto ? suggested : current.tenantId
      };
    });
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    setErrors((current) => ({ ...current, [name]: undefined }));
  };

  const validate = () => {
    const next = {};

    if (!form.tenantName.trim()) next.tenantName = 'Business name is required';

    if (!form.tenantId.trim()) {
      next.tenantId = 'Tenant ID is required';
    } else if (!/^[a-z0-9][a-z0-9-]{1,48}[a-z0-9]$/.test(form.tenantId.trim())) {
      next.tenantId =
        '3-50 characters: lowercase letters, numbers and hyphens, starting and ending with a letter or number';
    }

    if (!form.username.trim()) next.username = 'Username is required';
    else if (form.username.trim().length < 3) next.username = 'At least 3 characters';

    if (!form.email.trim()) next.email = 'Email is required';
    if (!form.password) next.password = 'Password is required';
    else if (form.password.length < 6) next.password = 'At least 6 characters';
    if (!form.address.trim()) next.address = 'Address is required';
    if (!form.phoneNumber.trim()) next.phoneNumber = 'Phone number is required';

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    try {
      const response = await api.post('/api/tenants', {
        ...form,
        tenantId: form.tenantId.trim().toLowerCase(),
        tenantPan: form.tenantPan.trim(),
        tenantMapsUrl: form.tenantMapsUrl.trim()
      });

      showNotification('success', response.data.message);
      setForm(EMPTY_FORM);
      setShowForm(false);
      await fetchTenants();
    } catch (error) {
      const message = error.response?.data?.message || 'Could not create the tenant';
      showNotification('error', message);

      // A duplicate id or a taken username is a field problem, not a page
      // problem - put it back on the field so it can be corrected in place.
      if (/tenant id|already exists/i.test(message)) setErrors({ tenantId: message });
      else if (/username/i.test(message)) setErrors({ username: message });
      else if (/email/i.test(message)) setErrors({ email: message });
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Scope the whole app to one tenant and drop into its till. Everything from
   * here on sends this tenant's id, so the super admin sees exactly what that
   * tenant's own staff would see.
   */
  const openTenant = (tenant) => {
    setTenantId(tenant.tenantId, tenant.tenantName);
    showNotification('success', `Viewing ${tenant.tenantName}`);
    navigate('/dashboard');
  };

  const startEdit = (tenant) => {
    setEditingId(tenant.tenantId);
    setEditForm({
      tenantName: tenant.tenantName,
      tenantPan: tenant.tenantPan || '',
      tenantMapsUrl: tenant.tenantMapsUrl || '',
      // Blank rather than 0 when unset, so an empty pair reads as "no location"
      tenantLatitude: tenant.tenantLatitude ?? '',
      tenantLongitude: tenant.tenantLongitude ?? ''
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm(EMPTY_EDIT);
  };

  const handleEditChange = (event) => {
    const { name, value } = event.target;
    setEditForm((current) => ({ ...current, [name]: value }));
  };

  /**
   * Everything about a business except its Tenant ID can be corrected here - a
   * name typed wrong at the counter or a PAN that only arrived after the shop
   * was registered should not need a new account to fix.
   */
  const saveEdit = async (tenant) => {
    const tenantName = editForm.tenantName.trim();
    const tenantPan = editForm.tenantPan.trim();
    const tenantMapsUrl = editForm.tenantMapsUrl.trim();
    const tenantLatitude = String(editForm.tenantLatitude ?? '').trim();
    const tenantLongitude = String(editForm.tenantLongitude ?? '').trim();

    if (!tenantName) {
      showNotification('error', 'Business name cannot be empty');
      return;
    }

    const unchanged =
      tenantName === tenant.tenantName &&
      tenantPan === (tenant.tenantPan || '') &&
      tenantMapsUrl === (tenant.tenantMapsUrl || '') &&
      tenantLatitude === String(tenant.tenantLatitude ?? '') &&
      tenantLongitude === String(tenant.tenantLongitude ?? '');

    if (unchanged) {
      cancelEdit();
      return;
    }

    setSavingEdit(true);
    try {
      const response = await api.patch(`/api/tenants/${tenant.tenantId}`, {
        tenantName,
        tenantPan,
        tenantMapsUrl,
        tenantLatitude,
        tenantLongitude
      });
      showNotification('success', response.data.message);

      // Patch the row in place rather than refetching the whole list, so the
      // scroll position and any search in progress survive the edit.
      setTenants((current) =>
        current.map((row) =>
          row.tenantId === tenant.tenantId
            ? {
                ...row,
                tenantName,
                tenantPan: response.data.tenantPan ?? tenantPan,
                tenantMapsUrl: response.data.tenantMapsUrl ?? tenantMapsUrl,
                tenantLatitude: response.data.tenantLatitude ?? null,
                tenantLongitude: response.data.tenantLongitude ?? null
              }
            : row
        )
      );
      cancelEdit();
    } catch (error) {
      showNotification('error', error.response?.data?.message || 'Could not save the business');
    } finally {
      setSavingEdit(false);
    }
  };

  const visibleTenants = tenants.filter((tenant) => {
    const query = search.trim().toLowerCase();
    if (!query) return true;
    return (
      tenant.tenantName.toLowerCase().includes(query) ||
      tenant.tenantId.toLowerCase().includes(query) ||
      (tenant.tenantPan || '').toLowerCase().includes(query)
    );
  });

  const totals = tenants.reduce(
    (sum, tenant) => ({
      users: sum.users + tenant.userCount,
      orders: sum.orders + tenant.orderCount,
      revenue: sum.revenue + (tenant.revenue || 0)
    }),
    { users: 0, orders: 0, revenue: 0 }
  );

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
      {/* Reset any of a business's login passwords, without entering its till */}
      {passwordTenant && (
        <TenantPasswordModal tenant={passwordTenant} onClose={() => setPasswordTenant(null)} />
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
            {isSalesAgent ? 'My Registrations' : 'System Dashboard'}
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            {isSalesAgent
              ? 'Register a business and hand the owner their login. Only the ones you registered show here.'
              : 'Create a business, then its admin signs in and starts selling.'}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={fetchTenants}
            disabled={loading}
            className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center text-cafe-600 hover:text-cafe-700 hover:bg-cafe-50 rounded-lg transition-colors"
            title="Refresh"
          >
            <RefreshCw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={() => setShowForm((current) => !current)}
            className="flex items-center justify-center px-4 py-2 min-h-[44px] whitespace-nowrap bg-cafe-600 text-white rounded-lg hover:bg-cafe-700 transition-colors"
          >
            <Plus className="h-5 w-5 mr-2" />
            New Business
          </button>
        </div>
      </div>

      {/* Totals across every tenant - or, for an agent, across their own */}
      <div
        className={`grid grid-cols-2 gap-3 sm:gap-4 mb-6 ${
          isSalesAgent ? 'lg:grid-cols-3' : 'lg:grid-cols-4'
        }`}
      >
        <SummaryTile
          icon={Building2}
          label={isSalesAgent ? 'Registered' : 'Businesses'}
          value={tenants.length}
        />
        <SummaryTile icon={Users} label="Users" value={totals.users} />
        <SummaryTile icon={ShoppingCart} label="Orders" value={totals.orders} />
        {/* What a shop takes is its own business - the API does not send it to
            an agent, so there is nothing to show. */}
        {!isSalesAgent && (
          <SummaryTile
            icon={Coffee}
            label="Revenue"
            value={`Rs ${totals.revenue.toLocaleString()}`}
          />
        )}
      </div>

      {activeTenantId && !isSalesAgent && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-start gap-2">
            <Eye className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-blue-800">
              You are currently scoped to <strong>{activeTenantId}</strong>. Every other screen
              shows only that business's data.
            </p>
          </div>
          <button
            onClick={() => {
              setTenantId('');
              showNotification('success', 'Cleared - viewing all businesses');
            }}
            className="flex-shrink-0 px-3 py-2 min-h-[44px] text-sm bg-white border border-blue-300 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors"
          >
            View all businesses
          </button>
        </div>
      )}

      {/* Create form */}
      {showForm && (
        <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 mb-6">
          <div className="flex items-center justify-between mb-1">
            <h2 className="text-lg font-semibold text-gray-900">New Business</h2>
            <button
              onClick={() => {
                setShowForm(false);
                setErrors({});
              }}
              className="text-gray-400 hover:text-gray-600 p-1"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <p className="text-sm text-gray-600 mb-5">
            This creates the business and its first admin account together. Give those login
            details to the owner - they sign in and can start selling immediately.
          </p>

          <form onSubmit={handleSubmit}>
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
              Business
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <Field
                label="Business name"
                name="tenantName"
                value={form.tenantName}
                onChange={handleNameChange}
                error={errors.tenantName}
                placeholder="Cafe Downtown"
              />
              <Field
                label="Tenant ID"
                name="tenantId"
                value={form.tenantId}
                onChange={handleChange}
                error={errors.tenantId}
                placeholder="cafe-downtown"
                hint="Permanent. Every order and item is stamped with this and it cannot be changed later."
              />
              {/* Plenty of shops register before their PAN comes through, so
                  this never blocks the account being opened - it can be filled
                  in later from the edit button on the list below. */}
              <Field
                label="PAN"
                name="tenantPan"
                value={form.tenantPan}
                onChange={handleChange}
                error={errors.tenantPan}
                placeholder="123456789"
                optional
                hint="Optional. Add it later if the business does not have one yet."
              />
              {/* Full width: the link and the capture button need the room, and
                  a half-width URL field truncates every real Maps link. */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Location <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <LocationFields
                  values={form}
                  onChange={(patch) => setForm((current) => ({ ...current, ...patch }))}
                />
              </div>
            </div>

            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
              Admin account for this business
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field
                label="Username"
                name="username"
                value={form.username}
                onChange={handleChange}
                error={errors.username}
                placeholder="downtown-admin"
              />
              <Field
                label="Email"
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                error={errors.email}
                placeholder="owner@example.com"
              />
              <Field
                label="Password"
                name="password"
                type="text"
                value={form.password}
                onChange={handleChange}
                error={errors.password}
                placeholder="At least 6 characters"
                hint="Shown in plain text so you can pass it on. They can change it after signing in."
              />
              <Field
                label="Phone number"
                name="phoneNumber"
                value={form.phoneNumber}
                onChange={handleChange}
                error={errors.phoneNumber}
                placeholder="+977 98..."
              />
              <div className="md:col-span-2">
                <Field
                  label="Address"
                  name="address"
                  value={form.address}
                  onChange={handleChange}
                  error={errors.address}
                  placeholder="Street, city"
                />
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 mt-6">
              <button
                type="submit"
                disabled={submitting}
                className="flex items-center justify-center px-5 py-2 min-h-[44px] bg-cafe-600 text-white rounded-lg hover:bg-cafe-700 disabled:opacity-60 transition-colors"
              >
                {submitting ? 'Creating...' : 'Create business'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setErrors({});
                }}
                className="px-5 py-2 min-h-[44px] border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* The master menu every new business is seeded from. Not an agent's
          call - they register businesses, the menu those start with is set
          here by the super admin. */}
      {!isSalesAgent && <DefaultMenuPanel onApplied={fetchTenants} />}

      {/* Tenant list */}
      <div className="bg-white rounded-lg shadow-md">
        <div className="p-4 sm:p-6 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <h2 className="text-lg font-semibold text-gray-900">
            Businesses {tenants.length > 0 && `(${tenants.length})`}
          </h2>
          {tenants.length > 0 && (
            <div className="relative">
              <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search businesses"
                className="w-full sm:w-64 pl-9 pr-3 py-2 min-h-[44px] border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cafe-500"
              />
            </div>
          )}
        </div>

        {loading ? (
          <div className="p-10 text-center text-gray-500">Loading businesses...</div>
        ) : loadError ? (
          <div className="p-6">
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
              <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-red-800">{loadError}</p>
                <button
                  onClick={fetchTenants}
                  className="mt-2 text-sm text-red-700 underline hover:no-underline"
                >
                  Try again
                </button>
              </div>
            </div>
          </div>
        ) : tenants.length === 0 ? (
          <div className="p-10 text-center">
            <Building2 className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <h3 className="text-lg font-medium text-gray-900">No businesses yet</h3>
            <p className="text-sm text-gray-600 mt-1 mb-4">
              Create the first one and hand its admin the login details.
            </p>
            <button
              onClick={() => setShowForm(true)}
              className="inline-flex items-center px-4 py-2 min-h-[44px] bg-cafe-600 text-white rounded-lg hover:bg-cafe-700 transition-colors"
            >
              <Plus className="h-5 w-5 mr-2" />
              New Business
            </button>
          </div>
        ) : visibleTenants.length === 0 ? (
          <div className="p-10 text-center text-gray-500">Nothing matches "{search}".</div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {visibleTenants.map((tenant) => (
              <li key={tenant.tenantId} className="p-4 sm:p-6">
                <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                  <div className="min-w-0 flex-1">
                    {editingId === tenant.tenantId ? (
                      /* Edited in place rather than in a dialog: the row it
                         belongs to stays visible, which is what tells you at a
                         glance that you are editing the right business. */
                      <div className="space-y-3">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">
                              Business name
                            </label>
                            <input
                              type="text"
                              name="tenantName"
                              value={editForm.tenantName}
                              onChange={handleEditChange}
                              onKeyDown={(event) => {
                                if (event.key === 'Enter') saveEdit(tenant);
                                if (event.key === 'Escape') cancelEdit();
                              }}
                              autoFocus
                              maxLength={100}
                              className="w-full px-3 py-2 min-h-[44px] border border-cafe-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-cafe-500"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">
                              PAN <span className="text-gray-400 font-normal">(optional)</span>
                            </label>
                            <input
                              type="text"
                              name="tenantPan"
                              value={editForm.tenantPan}
                              onChange={handleEditChange}
                              onKeyDown={(event) => {
                                if (event.key === 'Enter') saveEdit(tenant);
                                if (event.key === 'Escape') cancelEdit();
                              }}
                              maxLength={30}
                              placeholder="123456789"
                              className="w-full px-3 py-2 min-h-[44px] border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cafe-500"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">
                            Location <span className="text-gray-400 font-normal">(optional)</span>
                          </label>
                          <LocationFields
                            values={editForm}
                            onChange={(patch) =>
                              setEditForm((current) => ({ ...current, ...patch }))
                            }
                            compact
                          />
                        </div>
                        <p className="text-xs text-gray-500">
                          Tenant ID{' '}
                          <code className="bg-gray-100 px-1.5 py-0.5 rounded">{tenant.tenantId}</code>{' '}
                          cannot be changed - every order and item is stamped with it.
                        </p>
                        <div className="flex gap-2">
                          <button
                            onClick={() => saveEdit(tenant)}
                            disabled={savingEdit}
                            className="flex items-center px-3 py-2 min-h-[44px] bg-cafe-600 text-white rounded-lg hover:bg-cafe-700 disabled:opacity-60 transition-colors"
                          >
                            <Check className="h-4 w-4 mr-1" />
                            {savingEdit ? 'Saving...' : 'Save'}
                          </button>
                          <button
                            onClick={cancelEdit}
                            disabled={savingEdit}
                            className="px-3 py-2 min-h-[44px] border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-base font-semibold text-gray-900 truncate">
                          {tenant.tenantName}
                        </h3>
                        <button
                          onClick={() => startEdit(tenant)}
                          className="p-1.5 text-gray-400 hover:text-cafe-700 hover:bg-cafe-50 rounded transition-colors"
                          title="Edit this business"
                          aria-label={`Edit ${tenant.tenantName}`}
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        {/* Passwords are the super admin's call alone - an
                            agent registers the business but does not hold the
                            keys to its accounts afterwards. */}
                        {!isSalesAgent && (
                          <button
                            onClick={() => setPasswordTenant(tenant)}
                            className="p-1.5 text-gray-400 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors"
                            title="Reset a login password"
                            aria-label={`Reset a password for ${tenant.tenantName}`}
                          >
                            <KeyRound className="h-4 w-4" />
                          </button>
                        )}
                        <code className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                          {tenant.tenantId}
                        </code>
                        {tenant.tenantPan && (
                          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                            PAN {tenant.tenantPan}
                          </span>
                        )}
                        {mapsLinkFor(tenant) && (
                          <a
                            href={mapsLinkFor(tenant)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs bg-cafe-50 text-cafe-700 px-2 py-0.5 rounded hover:bg-cafe-100 transition-colors"
                            title="Open this business on the map"
                          >
                            <MapPin className="h-3.5 w-3.5" />
                            Map
                          </a>
                        )}
                        {tenant.activeUserCount === 0 && (
                          <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full">
                            No active users
                          </span>
                        )}
                      </div>
                    )}
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-gray-600">
                      <span>{tenant.userCount} users</span>
                      <span>{tenant.itemCount} menu items</span>
                      <span>{tenant.orderCount} orders</span>
                      {tenant.revenue !== undefined && (
                        <span className="font-medium text-gray-900">
                          Rs {tenant.revenue.toLocaleString()}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Dropping into a shop's till is a super admin's job. An
                      agent has no tenant of their own, so the API refuses every
                      till route for them anyway - the button would only ever
                      produce an error. */}
                  {!isSalesAgent && (
                    <button
                      onClick={() => openTenant(tenant)}
                      className="flex items-center justify-center flex-shrink-0 px-4 py-2 min-h-[44px] bg-cafe-600 text-white rounded-lg hover:bg-cafe-700 transition-colors"
                    >
                      <LogIn className="h-4 w-4 mr-2" />
                      Open
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/**
 * The default menu: items every newly created business starts with. Managed
 * here because this is where businesses are created - the two belong on the
 * same screen. "Apply to all businesses" is for shops registered before an
 * item was added; it only fills gaps and never touches items a shop already
 * has.
 */
function DefaultMenuPanel({ onApplied }) {
  const EMPTY_ITEM = { name: '', nameNepali: '', price: '', image: null };

  // Open from the start: registering businesses and curating this menu are the
  // only two jobs on this screen, so there is nothing to collapse it out of the
  // way of. It still folds shut once the list gets long.
  const [open, setOpen] = useState(true);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [form, setForm] = useState(EMPTY_ITEM);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_ITEM);
  const [applying, setApplying] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/default-items');
      setItems(response.data || []);
      setLoaded(true);
    } catch (error) {
      showNotification('error', error.response?.data?.message || 'Could not load the default menu');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetched with the page, not on first open - the item count sits in the
  // panel header, so collapsing it must not leave that heading blank.
  useEffect(() => {
    if (!loaded) load();
  }, [loaded, load]);

  const handleAdd = async (event) => {
    event.preventDefault();

    const price = parseFloat(form.price);
    if (!form.name.trim() || !Number.isFinite(price) || price < 0) {
      showNotification('error', 'An item needs a name and a price of zero or more.');
      return;
    }

    try {
      setSaving(true);
      const response = await api.post('/api/default-items', {
        name: form.name.trim(),
        nameNepali: form.nameNepali.trim(),
        price,
        image: form.image
      });

      // The count comes back from the fan-out, so this says what actually
      // happened rather than promising something that may have failed.
      const reached = response.data?.tenantsUpdated || 0;
      showNotification(
        'success',
        `'${form.name.trim()}' added to the default menu` +
          (reached > 0 ? ` and to ${reached} business${reached === 1 ? '' : 'es'}` : '')
      );
      if (onApplied) onApplied();
      setForm(EMPTY_ITEM);
      await load();
    } catch (error) {
      showNotification('error', error.response?.data?.message || 'Could not add the item');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (item) => {
    setEditingId(item._id);
    setEditForm({
      name: item.name,
      nameNepali: item.nameNepali || '',
      price: String(item.price),
      image: item.image || null
    });
  };

  const saveEdit = async (item) => {
    const price = parseFloat(editForm.price);
    if (!editForm.name.trim() || !Number.isFinite(price) || price < 0) {
      showNotification('error', 'An item needs a name and a price of zero or more.');
      return;
    }

    try {
      setSaving(true);
      const response = await api.put(`/api/default-items/${item._id}`, {
        name: editForm.name.trim(),
        nameNepali: editForm.nameNepali.trim(),
        price,
        image: editForm.image
      });

      // Worth saying out loud - an edit here reaches live menus, and the number
      // is smaller than the shop count when some shops set their own price.
      const reached = response.data?.tenantsUpdated || 0;
      showNotification(
        'success',
        reached > 0
          ? `'${editForm.name.trim()}' updated on ${reached} business${reached === 1 ? '' : 'es'}`
          : `'${editForm.name.trim()}' updated`
      );

      setEditingId(null);
      await load();
      if (onApplied) onApplied();
    } catch (error) {
      showNotification('error', error.response?.data?.message || 'Could not save the item');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (
      !window.confirm(
        `Remove '${item.name}' from the default menu? Businesses that already have it keep their copy.`
      )
    ) {
      return;
    }

    try {
      await api.delete(`/api/default-items/${item._id}`);
      showNotification('success', `'${item.name}' removed`);
      await load();
    } catch (error) {
      showNotification('error', error.response?.data?.message || 'Could not remove the item');
    }
  };

  const handleApply = async () => {
    if (
      !window.confirm(
        `Re-sync the ${items.length} default item${items.length === 1 ? '' : 's'} across every business? ` +
          'Missing items are added and default prices and photos are refreshed. ' +
          'Anything a business has hidden, deleted or repriced itself is left alone.'
      )
    ) {
      return;
    }

    try {
      setApplying(true);
      const response = await api.post('/api/default-items/apply', {});
      showNotification('success', response.data.message);
      if (onApplied) onApplied();
    } catch (error) {
      showNotification('error', error.response?.data?.message || 'Could not apply the menu');
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md mb-6">
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-3 p-4 sm:p-6 text-left hover:bg-gray-50 transition-colors rounded-lg"
      >
        <div className="flex items-center gap-3 min-w-0">
          <UtensilsCrossed className="h-5 w-5 text-cafe-600 flex-shrink-0" />
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-gray-900">
              Default Menu {loaded && items.length > 0 ? `(${items.length})` : ''}
            </h2>
            <p className="text-sm text-gray-600 truncate">
              Every new business starts with these items on its menu.
            </p>
          </div>
        </div>
        <ChevronDown
          className={`h-5 w-5 text-gray-400 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div className="border-t border-gray-100 p-4 sm:p-6 space-y-4">
          {/* Add form */}
          <form onSubmit={handleAdd} className="flex flex-col sm:flex-row sm:items-start gap-2">
            <DefaultItemPhoto
              value={form.image}
              onChange={(image) => setForm((current) => ({ ...current, image }))}
              disabled={saving}
            />
            <input
              type="text"
              value={form.name}
              onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
              placeholder="Item name (e.g. Milk Tea)"
              className="flex-1 min-w-0 px-3 py-2 min-h-[44px] border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cafe-500"
            />
            <input
              type="text"
              value={form.nameNepali}
              onChange={(event) =>
                setForm((current) => ({ ...current, nameNepali: event.target.value }))
              }
              placeholder="Nepali name (optional)"
              className="flex-1 min-w-0 px-3 py-2 min-h-[44px] border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cafe-500"
            />
            <div className="flex gap-2">
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.price}
                onChange={(event) =>
                  setForm((current) => ({ ...current, price: event.target.value }))
                }
                placeholder="Price"
                className="w-28 px-3 py-2 min-h-[44px] border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cafe-500"
              />
              <button
                type="submit"
                disabled={saving}
                className="flex items-center justify-center px-4 py-2 min-h-[44px] bg-cafe-600 text-white rounded-lg hover:bg-cafe-700 disabled:opacity-60 transition-colors whitespace-nowrap"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add
              </button>
            </div>
          </form>

          {/* Item list */}
          {loading ? (
            <p className="text-sm text-gray-500 py-4 text-center">Loading default menu...</p>
          ) : items.length === 0 ? (
            <p className="text-sm text-gray-500 py-4 text-center">
              Nothing yet. Items added here appear on every business's menu straight away.
            </p>
          ) : (
            <ul className="divide-y divide-gray-100 border border-gray-100 rounded-lg">
              {items.map((item) => (
                <li key={item._id} className="p-3 flex items-center gap-3">
                  {editingId === item._id ? (
                    <div className="flex-1 flex flex-col sm:flex-row sm:items-start gap-2">
                      <DefaultItemPhoto
                        value={editForm.image}
                        onChange={(image) => setEditForm((current) => ({ ...current, image }))}
                        disabled={saving}
                      />
                      <input
                        type="text"
                        value={editForm.name}
                        onChange={(event) =>
                          setEditForm((current) => ({ ...current, name: event.target.value }))
                        }
                        autoFocus
                        className="flex-1 min-w-0 px-3 py-2 min-h-[44px] border border-cafe-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-cafe-500"
                      />
                      <input
                        type="text"
                        value={editForm.nameNepali}
                        onChange={(event) =>
                          setEditForm((current) => ({ ...current, nameNepali: event.target.value }))
                        }
                        placeholder="Nepali name"
                        className="flex-1 min-w-0 px-3 py-2 min-h-[44px] border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cafe-500"
                      />
                      <div className="flex gap-2">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={editForm.price}
                          onChange={(event) =>
                            setEditForm((current) => ({ ...current, price: event.target.value }))
                          }
                          className="w-24 px-3 py-2 min-h-[44px] border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cafe-500"
                        />
                        <button
                          onClick={() => saveEdit(item)}
                          disabled={saving}
                          className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center text-white bg-cafe-600 hover:bg-cafe-700 rounded-lg transition-colors"
                          title="Save"
                          aria-label={`Save ${item.name}`}
                        >
                          <Check className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setEditingId(null)}
                          disabled={saving}
                          className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center text-gray-500 border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors"
                          title="Cancel"
                          aria-label="Cancel editing"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      {item.image ? (
                        <img
                          src={item.image}
                          alt=""
                          className="h-10 w-10 flex-shrink-0 rounded-lg object-cover border border-gray-200"
                        />
                      ) : (
                        <div className="h-10 w-10 flex-shrink-0 rounded-lg border border-dashed border-gray-200 flex items-center justify-center">
                          <ImageIcon className="h-4 w-4 text-gray-300" />
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {item.name}
                          {item.nameNepali ? (
                            <span className="text-gray-500 font-normal"> · {item.nameNepali}</span>
                          ) : null}
                        </p>
                      </div>
                      <span className="text-sm font-semibold text-gray-900 whitespace-nowrap">
                        Rs {item.price.toFixed(2)}
                      </span>
                      <button
                        onClick={() => startEdit(item)}
                        className="p-1.5 text-gray-400 hover:text-cafe-700 hover:bg-cafe-50 rounded transition-colors"
                        title="Edit"
                        aria-label={`Edit ${item.name}`}
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(item)}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                        title="Remove"
                        aria-label={`Remove ${item.name}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}

          {/* A repair tool, not the delivery mechanism - adding or editing an
              item above already reaches every shop. This replays the lot for a
              business whose menu got out of step. */}
          {items.length > 0 && (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pt-1">
              <p className="text-xs text-gray-500">
                Every business gets these automatically, and each can hide or reprice its own copy.
                Use this to re-sync a business whose menu got out of step.
              </p>
              <button
                onClick={handleApply}
                disabled={applying}
                className="flex items-center justify-center px-4 py-2 min-h-[44px] border border-cafe-600 text-cafe-700 rounded-lg hover:bg-cafe-50 disabled:opacity-60 transition-colors whitespace-nowrap text-sm font-semibold"
              >
                {applying ? 'Applying...' : 'Apply to all businesses'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * A thumbnail-sized photo picker for one default menu item.
 *
 * The shared ImageUpload component is a half-page drop zone, which is right on
 * the item form a shop fills in one item at a time and wrong in a list of forty
 * rows - so this is the same upload endpoint at the size the list can carry.
 *
 * Clearing a photo only detaches it here. The file sits in the shared folder
 * rather than a tenant's, and deleting it would pull the picture out from under
 * every shop already seeded with it.
 */
function DefaultItemPhoto({ value, onChange, disabled = false, size = 'h-14 w-14' }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      showNotification('error', 'That is not an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      showNotification('error', 'Photos must be under 5MB');
      return;
    }

    try {
      setUploading(true);
      const data = new FormData();
      data.append('image', file);
      const response = await api.post('/api/items/upload-image', data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      onChange(response.data.imageUrl);
    } catch (error) {
      showNotification('error', error.response?.data?.message || 'Could not upload the photo');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className={`relative flex-shrink-0 ${size}`}>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={handleFile}
        className="hidden"
        disabled={disabled || uploading}
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={disabled || uploading}
        title={value ? 'Replace photo' : 'Add a photo'}
        aria-label={value ? 'Replace photo' : 'Add a photo'}
        className={`${size} rounded-lg border overflow-hidden flex items-center justify-center transition-colors ${
          value
            ? 'border-gray-200'
            : 'border-dashed border-gray-300 hover:border-cafe-400 hover:bg-cafe-50'
        } disabled:opacity-60`}
      >
        {uploading ? (
          <RefreshCw className="h-4 w-4 text-gray-400 animate-spin" />
        ) : value ? (
          <img src={value} alt="" className="h-full w-full object-cover" />
        ) : (
          <Upload className="h-4 w-4 text-gray-400" />
        )}
      </button>

      {value && !disabled && !uploading && (
        <button
          type="button"
          onClick={() => onChange(null)}
          title="Remove photo"
          aria-label="Remove photo"
          className="absolute -top-1.5 -right-1.5 bg-white border border-gray-300 text-gray-500 hover:text-red-600 rounded-full p-0.5 shadow-sm"
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}

/**
 * Every login of one business, each with a reset box. The new password is
 * typed in plain text on purpose - the whole point is to hand it to the owner
 * over the phone, exactly like the create-business form does. The reset itself
 * goes through the same auth route the tenant's own admin would use.
 */
function TenantPasswordModal({ tenant, onClose }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [passwords, setPasswords] = useState({});
  const [savingId, setSavingId] = useState(null);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        const response = await api.get(`/api/tenants/${tenant.tenantId}/users`);
        if (!cancelled) {
          setUsers(response.data.users || []);
          setLoadError(null);
        }
      } catch (error) {
        if (!cancelled) {
          setLoadError(error.response?.data?.message || 'Could not load the users');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [tenant.tenantId]);

  const resetPassword = async (target) => {
    const newPassword = (passwords[target._id] || '').trim();

    if (newPassword.length < 6) {
      showNotification('error', 'Password must be at least 6 characters long');
      return;
    }

    try {
      setSavingId(target._id);
      await api.patch(`/api/auth/users/${target._id}/reset-password`, { newPassword });
      showNotification(
        'success',
        `${target.username}'s password is set - hand it to them, they can change it after signing in.`
      );
      setPasswords((current) => ({ ...current, [target._id]: '' }));
    } catch (error) {
      showNotification('error', error.response?.data?.message || 'Could not reset the password');
    } finally {
      setSavingId(null);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title={`Passwords · ${tenant.tenantName}`} size="lg">
      <div className="p-4 sm:p-6 space-y-3">
        <p className="text-sm text-gray-600">
          Set a new password for any of this business's logins, then pass it on. The password is
          shown as you type so you can read it out - they can change it themselves afterwards.
        </p>

        {loading ? (
          <p className="text-sm text-gray-500 py-6 text-center">Loading logins...</p>
        ) : loadError ? (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
            <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800">{loadError}</p>
          </div>
        ) : users.length === 0 ? (
          <p className="text-sm text-gray-500 py-6 text-center">
            This business has no logins to reset.
          </p>
        ) : (
          <ul className="divide-y divide-gray-100 border border-gray-100 rounded-lg">
            {users.map((row) => (
              <li key={row._id} className="p-3 space-y-2">
                <div className="flex items-center gap-2 flex-wrap min-w-0">
                  <span className="text-sm font-semibold text-gray-900">{row.username}</span>
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full ${
                      row.role === 'tenant_admin'
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {row.role === 'tenant_admin' ? 'Admin' : 'Staff'}
                  </span>
                  {!row.isActive && (
                    <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full">
                      Disabled
                    </span>
                  )}
                  <span className="text-xs text-gray-500 truncate">{row.email}</span>
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    type="text"
                    value={passwords[row._id] || ''}
                    onChange={(event) =>
                      setPasswords((current) => ({ ...current, [row._id]: event.target.value }))
                    }
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') resetPassword(row);
                    }}
                    placeholder="New password (at least 6 characters)"
                    autoComplete="new-password"
                    className="flex-1 min-w-0 px-3 py-2 min-h-[44px] border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cafe-500"
                  />
                  <button
                    onClick={() => resetPassword(row)}
                    disabled={savingId === row._id}
                    className="flex items-center justify-center px-4 py-2 min-h-[44px] bg-cafe-600 text-white rounded-lg hover:bg-cafe-700 disabled:opacity-60 transition-colors text-sm font-semibold whitespace-nowrap"
                  >
                    <KeyRound className="h-4 w-4 mr-1.5" />
                    {savingId === row._id ? 'Saving...' : 'Reset'}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  );
}

/**
 * Where the business is: its Google listing, its coordinates, or both.
 *
 * Two ways in, because agents register businesses in two situations. Standing
 * in the shop, "Use current location" is one tap and needs nothing from the
 * owner. On a phone call, the owner sends their Google Maps link - which is
 * worth more than a pin, since it carries the shop's own name and photos.
 *
 * Both are optional. A business must never fail to register because a phone
 * refused a GPS fix.
 */
function LocationFields({ values, onChange, compact = false }) {
  const [locating, setLocating] = useState(false);
  const [status, setStatus] = useState(null);

  const hasCoords =
    String(values.tenantLatitude ?? '').trim() !== '' &&
    String(values.tenantLongitude ?? '').trim() !== '';

  const preview = mapsLinkFor({
    tenantMapsUrl: values.tenantMapsUrl,
    tenantLatitude: hasCoords ? Number(values.tenantLatitude) : null,
    tenantLongitude: hasCoords ? Number(values.tenantLongitude) : null
  });

  const useCurrentLocation = () => {
    // Browsers only hand out a position over HTTPS (localhost aside), which is
    // the usual reason this is missing rather than the device lacking GPS.
    if (!navigator.geolocation) {
      setStatus({
        tone: 'error',
        text: 'This browser cannot share a location. Paste a Google Maps link instead.'
      });
      return;
    }

    setLocating(true);
    setStatus(null);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const latitude = Number(position.coords.latitude.toFixed(6));
        const longitude = Number(position.coords.longitude.toFixed(6));

        onChange({
          tenantLatitude: String(latitude),
          tenantLongitude: String(longitude),
          // The generated link is only a fallback: a Google listing the owner
          // sent is better, so anything already typed is left alone.
          tenantMapsUrl:
            (values.tenantMapsUrl || '').trim() ||
            `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`
        });

        const accuracy = Math.round(position.coords.accuracy || 0);
        setStatus({
          tone: 'success',
          text: accuracy ? `Location captured - accurate to about ${accuracy}m.` : 'Location captured.'
        });
        setLocating(false);
      },
      (error) => {
        const reason =
          error.code === error.PERMISSION_DENIED
            ? 'Location permission was refused. Allow it in the browser, or paste a Google Maps link.'
            : error.code === error.POSITION_UNAVAILABLE
              ? 'No position available right now. Try again outside, or paste a Google Maps link.'
              : 'Getting a location took too long. Try again, or paste a Google Maps link.';

        setStatus({ tone: 'error', text: reason });
        setLocating(false);
      },
      // A shop only needs to be found, not navigated to, so a cached fix from
      // the last few minutes is fine and answers far faster.
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 300000 }
    );
  };

  const clearCoords = () => {
    onChange({ tenantLatitude: '', tenantLongitude: '' });
    setStatus(null);
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-col sm:flex-row gap-2">
        <input
          type="url"
          name="tenantMapsUrl"
          value={values.tenantMapsUrl}
          onChange={(event) => onChange({ tenantMapsUrl: event.target.value })}
          placeholder="https://maps.app.goo.gl/..."
          maxLength={500}
          autoComplete="off"
          aria-label="Google Maps link"
          className="flex-1 min-w-0 px-3 py-2 min-h-[44px] border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cafe-500"
        />
        <button
          type="button"
          onClick={useCurrentLocation}
          disabled={locating}
          className="flex items-center justify-center gap-1.5 px-4 py-2 min-h-[44px] border border-cafe-600 text-cafe-700 rounded-lg hover:bg-cafe-50 disabled:opacity-60 transition-colors whitespace-nowrap text-sm font-semibold"
        >
          <LocateFixed className={`h-4 w-4 ${locating ? 'animate-pulse' : ''}`} />
          {locating ? 'Locating...' : 'Use current location'}
        </button>
      </div>

      {hasCoords && (
        <div className="flex items-center gap-2 flex-wrap text-xs text-gray-600">
          <span className="inline-flex items-center gap-1 bg-gray-100 px-2 py-1 rounded">
            <MapPin className="h-3.5 w-3.5 text-cafe-600" />
            {Number(values.tenantLatitude).toFixed(6)}, {Number(values.tenantLongitude).toFixed(6)}
          </span>
          <button
            type="button"
            onClick={clearCoords}
            className="text-gray-500 hover:text-red-600 underline"
          >
            Clear
          </button>
        </div>
      )}

      {preview && (
        <a
          href={preview}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-xs text-cafe-700 hover:underline"
        >
          <ExternalLink className="h-3.5 w-3.5" />
          Check this opens the right place
        </a>
      )}

      {status && (
        <p className={`text-xs ${status.tone === 'error' ? 'text-red-600' : 'text-green-700'}`}>
          {status.text}
        </p>
      )}

      {!compact && (
        <p className="text-gray-500 text-xs">
          Optional. Press the button while you are at the shop, or paste the owner's Google Maps
          link.
        </p>
      )}
    </div>
  );
}

/** Mirrors the id the name field would have produced, so hand edits are detectable. */
function slugify(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 50);
}

function SummaryTile({ icon: Icon, label, value }) {
  return (
    <div className="bg-white rounded-lg shadow-md p-4">
      <div className="flex items-center gap-2 text-gray-500 mb-1">
        <Icon className="h-4 w-4 flex-shrink-0" />
        <span className="text-xs uppercase tracking-wide truncate">{label}</span>
      </div>
      <p className="text-xl sm:text-2xl font-bold text-gray-900 truncate">{value}</p>
    </div>
  );
}

function Field({ label, name, value, onChange, error, placeholder, hint, optional = false, type = 'text' }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}
        {optional ? <span className="text-gray-400 font-normal"> (optional)</span> : ' *'}
      </label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        autoComplete="off"
        className={`w-full px-3 py-2 min-h-[44px] border rounded-lg focus:outline-none focus:ring-2 focus:ring-cafe-500 ${
          error ? 'border-red-500' : 'border-gray-300'
        }`}
      />
      {error ? (
        <p className="text-red-500 text-sm mt-1">{error}</p>
      ) : hint ? (
        <p className="text-gray-500 text-xs mt-1">{hint}</p>
      ) : null}
    </div>
  );
}

export default SystemDashboard;
